import { useRef, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { routeAfterSession } from "@/shared/session/onboarding";
import { DataOriginBadge, ErrorState, Icon, LoadingState, Page } from "@/shared/ui";
import { AdoptFlow } from "./adoptFlow";
import { BrandLogo } from "@/shared/ui/BrandLogo";
import { isOriginalCharacter } from "./residentView";
import { ResidentLife } from "./life/ResidentLife";
import "./planet.css";
import "./resident-profile.css";

/**
 * 居民公开主页（/world/residents/:petId，不登录也能看）＝「TA 的星球生活」（UI-WORLD-LIVING-117-3，参考稿 05-life-A）：
 * 首屏 TA 的家、名字和 TA 自己的一句话、TA 的一天、TA 的朋友、TA 的照片、TA 的梦想（./life/ResidentLife），最后才是「把 TA 带回家」（页末，不贴底）。
 * 只写后台给的公开事实，没有的那一节不出。
 * 领养：访客注册 / 登录时带上“想领养这一位”，登录后仍由本人确认；已登录、TA 可以领养时用共用的“迎接 TA”（adoptFlow.tsx：
 * 没家新建、一个家直接迎进、几个家先选、不是管理员说清楚；点了先确认，确认后才领养）。已登录的出口始终有“回到我的家”和“再看看其他居民”。
 * PetSoul 原创角色（resident.resident_kind = public_npc，2026-09-27 D5）：名字旁标“PetSoul 原创角色”（和物种同一种小标签，读屏照读），
 * 不给任何领养入口——即便数据里 adoptable 写错成 true 也不给；说明那句写“TA 是 PetSoul 原创角色，不参加领养。”。字段缺省当可以领养的居民。
 */
/** Same normal-click/history rules as WorldHero（首屏是 TA 自己的家，见 ./life/LifeScene，不是通用风景）。
 *  访客的「返回」是普通链接（不走浏览器后退）：带上 fromResident，「先逛逛」才知道这是从居民主页回来的、接着看刚才的面板（UI-WORLD-LIVING-117-2）。 */
function ResidentHeader({ back, historyBack }: { back: string; historyBack: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  // 从另一位居民主页的「TA 最近见过的」点进来（117-3，带 backTo）：「返回」回到那一位，不回星球
  const backTo = (location.state as { backTo?: unknown } | null)?.backTo;
  const to = typeof backTo === "string" && backTo.startsWith("/world/residents/") ? backTo : back;
  const onBack = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!historyBack || location.key === "default" || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(-1);
  };
  return <header className="ps-resident-profile__nav">
    <Link to={to} state={historyBack || to !== back ? undefined : { fromResident: true }} className="ps-resident-profile__back" aria-label="返回" onClick={onBack}><Icon name="back" size={20} /></Link>
    <BrandLogo size="compact" />
  </header>;
}

/**
 * 领养说明：放在面板里、页末行动区的上面。只给访客说“注册后由你确认才领养”；已登录的人不看写给访客的话，
 * 迎接的说明由共用的“迎接 TA”自己给（按钮、确认框、不是管理员时的一句话），这里不再重复，也不写“仍在接入中”这类过时的说法。
 * 自家的伙伴（profile.is_own，例如刚领养完从入住等待页点进来）：说“TA 已经是你家的伙伴了。”，不对领养人说“不能被领养”（巡检 R8-A03）；
 * 别家的人看到的“TA 暂时不能被领养。”不变。
 */
function AdoptNote({ adoptable, signedIn, own, original }: { adoptable: boolean; signedIn: boolean; own: boolean; original: boolean }) {
  if (own) return <p className="ps-resident-page__note">TA 已经是你家的伙伴了。</p>;
  if (original) return <p className="ps-resident-page__note">TA 是 PetSoul 原创角色，不参加领养。</p>;
  if (!adoptable) return <p className="ps-resident-page__note">TA 暂时不能被领养。</p>;
  if (!signedIn) return <p className="ps-resident-page__note">注册后由你确认才领养，不会自动领养；刚才选中的伙伴会被记住。</p>;
  return null;
}

export function PublicPetPage() {
  const { petId } = useParams();
  const [params] = useSearchParams();
  const guest = params.get("view") === "guest";
  const { pets, session: sessionService } = useServices();
  // Explicit guest intent never starts a session read or changes mode after a session/detail error.
  const session = useQuery({ queryKey: queryKeys.session, queryFn: () => sessionService.current(), enabled: !guest, staleTime: 30_000 });
  const userId = !guest && session.isSuccess && session.data?.authenticated ? session.data.user?.user_id : undefined;
  const memberReady = Boolean(userId?.trim());
  const memberSeen = useRef(false);
  if (memberReady) memberSeen.current = true;
  // Preserve existing unmarked public deep links only after the session explicitly confirms anonymous.
  // This uses the original strict client, in its own cache, never as a fallback from explicit guest/errors.
  const anonymousReady = !guest && !memberSeen.current && session.isSuccess && session.data?.authenticated === false;
  const identityReady = guest || memberReady || anonymousReady;
  const guestAvailable = !guest || typeof pets.publicPetGuest === "function";
  const pet = useQuery({
    queryKey: guest ? queryKeys.publicPetGuest(petId ?? "-")
      : memberReady ? queryKeys.publicPetMember(userId!, petId ?? "-")
        : [...queryKeys.publicPet(petId ?? "-"), anonymousReady ? "session-anonymous" : "identity-pending"],
    queryFn: async () => {
      if (guest) {
        if (!pets.publicPetGuest) throw ApiError.capability("public.guest_read");
        return pets.publicPetGuest(petId!);
      }
      if (!identityReady) throw ApiError.capability("public.confirmed_viewer");
      return pets.publicPet(petId!);
    },
    enabled: Boolean(petId) && identityReady && guestAvailable,
    staleTime: 30_000,
    ...(guest ? { retry: false as const, refetchOnWindowFocus: false as const, refetchOnReconnect: false as const } : {}),
  });
  // Query errors can retain old data; 404/withdrawal/unknown must not leave a cached profile/action area.
  const data = identityReady && guestAvailable && pet.isSuccess ? pet.data : undefined;
  const signedIn = !guest && env.dataMode === "live" && memberReady;
  const needsCompanion = signedIn && session.data?.onboarding?.step === "needs_companion";
  const home = signedIn && session.data ? routeAfterSession(session.data) : "/welcome";
  const notFound = !petId || (pet.isError && toApiError(pet.error).status === 404);
  const name = data?.profile.display_name ?? "";
  const entry = petId ? `entry=adopt&pet_id=${encodeURIComponent(petId)}` : "";
  const own = signedIn && Boolean(data?.profile.is_own);
  const worldPath = guest ? "/world?view=guest" : "/world";
  // PetSoul 原创角色不参加领养：数据里 adoptable 就算写错成 true，也不给任何领养入口（resident_kind 缺省当可以领养的居民）
  const original = isOriginalCharacter(data?.resident);
  const adoptable = Boolean(data?.adoptable) && !original;
  const canWelcome = Boolean(!own && adoptable && data?.resident);
  return (
    <Page bare className="ps-world-page ps-resident-page ps-resident-profile">
      <ResidentHeader back={worldPath} historyBack={!guest} />
      <section className="ps-world-sheet ps-resident-page__sheet" aria-labelledby="resident-title">
        {notFound ? (
          <div className="ps-resident-page__missing">
            <h1 id="resident-title">没有找到这位居民的公开主页</h1>
            <p>TA 可能已经有了自己的家，或者主页没有公开。</p>
          </div>
        ) : !guestAvailable ? (
          <>
            <h1 id="resident-title" className="ps-resident-page__error-title">暂时打不开这位居民的主页</h1>
            <ErrorState error={ApiError.capability("public.guest_read")} />
          </>
        ) : !guest && session.isError ? (
          <>
            <h1 id="resident-title" className="ps-resident-page__error-title">暂时无法确认登录状态</h1>
            <ErrorState error={session.error} onRetry={() => void session.refetch()} />
          </>
        ) : !identityReady && session.isSuccess ? (
          <>
            <h1 id="resident-title" className="ps-resident-page__error-title">暂时无法确认登录状态</h1>
            <Link className="ps-btn ps-btn--secondary" to="/login">去登录</Link>
          </>
        ) : pet.isPending ? (
          <>
            <h1 id="resident-title" className="visually-hidden">正在打开居民主页</h1>
            <LoadingState lines={3} label="正在打开居民主页…" />
          </>
        ) : pet.isError ? (
          <>
            <h1 id="resident-title" className="ps-resident-page__error-title">暂时打不开这位居民的主页</h1>
            <ErrorState error={pet.error} onRetry={() => void pet.refetch()} />
          </>
        ) : data ? (
          <>
            {/* 按居民编号换一个主体：从「TA 最近见过的」点进另一位时，展开、照片全看这些状态不带进去（评审 R3 中 1） */}
            <ResidentLife key={petId ?? ""} view={data} guest={guest} petId={petId ?? ""} />
            <AdoptNote adoptable={adoptable} signedIn={signedIn} own={own} original={original} />
            <DataOriginBadge origin={data.profile.data_origin} label="演示居民" />
          </>
        ) : null}
      </section>
      {data || notFound ? (
        <footer className="ps-world-cta">
          {signedIn ? (
            <>
              {/* 已登录、TA 可以领养：共用的“迎接 TA”，本人已经入住的也用它迎进自己的家。自家的伙伴只给“回到我的家”，守卫按入住状态带去该去的地方 */}
              {canWelcome && data?.resident ? <AdoptFlow candidateId={data.resident.candidate_id} petId={petId} name={name} adoptable={adoptable} block presence={data.resident.presence} backAt={data.resident.back_at} /> : null}
              <Link className={canWelcome ? "ps-world-cta__home" : "ps-btn ps-btn--primary ps-btn--block"} to={home}>
                {needsCompanion && !own ? "继续寻找我的 TA" : "回到我的家"}
              </Link>
            </>
          ) : adoptable ? (
            <>
              <Link className="ps-btn ps-btn--primary ps-btn--block" to={`/register?${entry}`}>
                把{name}带回家
              </Link>
              <Link className="ps-world-cta__login" to={`/login?${entry}`}>
                已经有账号？登录后继续
              </Link>
            </>
          ) : (
            <>
              <Link className="ps-btn ps-btn--primary ps-btn--block" to="/register">
                寻找我的 TA
              </Link>
              <Link className="ps-world-cta__login" to="/login">
                已经有 TA 了？登录
              </Link>
            </>
          )}
        </footer>
      ) : null}
      <Link className="ps-resident-page__more" to={worldPath}>
        再看看其他居民 <Icon name="chevron" size={16} />
      </Link>
    </Page>
  );
}
